import { GQL_ID } from '@@partner:types:graphql'
import React, { useEffect, useState } from 'react'
import { MainPageBlockForm } from '../form/MainPageConfigFormState'
import { CheckTreePicker, Input, Panel, SelectPicker } from 'rsuite'
import { arrayMove, useSortable } from '@dnd-kit/sortable'
import { Category } from '@@partner:types:app'
import SortIcon from '@rsuite/icons/Sort'
import { CSS } from '@dnd-kit/utilities'
import { closestCenter, DndContext, DragEndEvent } from '@dnd-kit/core'
import { useCategoriesFetcher } from '@@partner:graphql/query/catalog/categoriesWithParent.gql'
import { ItemDataType } from 'rsuite/esm/MultiCascadeTree'
import { usePartner } from '@@partner:helpers/hooks/auth'
import EntityLink from './EntityLink'

type CategoryBlockProps = {
  block: MainPageBlockForm
  onChange: (block: MainPageBlockForm) => void
}


const mapToItemDataType = (category: Category) => ({
  label: category.name,
  value: category.id,
  children: [],
  loading: false,
  parent_id: category.parent_id,
  __category: category
})

type SelectedCategoryProps = {
  id: GQL_ID
  name: string
  order: number
  previewSize: 'standard' | 'double' | 'big'
}

export default function CategoryBlock ({
  block,
  onChange
}: CategoryBlockProps) {
  const { brandSlug } = usePartner()
  const [ data, setData ] = useState<ItemDataType[]>([])

  const selectedCategories = block.payload?.categories as SelectedCategoryProps[] ?? []

  const { fetchCategories, fetchRootCategories } = useCategoriesFetcher()
  
  useEffect(() => {
    const preload = async () => {
      const initial = await fetchRootCategories({
        skipIds: []
      })
      setData(initial.categories.edges.map(mapToItemDataType))
    }
    preload()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleChangePayloadCategories = (categories: SelectedCategoryProps[]) => {
    onChange({
      ...block,
      payload: {
        ...block.payload,
        categories: categories.map((cat, index) => ({
          ...cat,
          order: index + 1
        }))
      }
    })
  }

  const handleLoadRootCategories = async () => {
    const initial = await fetchRootCategories({ skipIds: []})
    setData(initial.categories.edges.map(mapToItemDataType))
  }

  const handleGetChildren = async (node: ItemDataType) => {
    if (node.value) {
      const data = await fetchCategories({
        parentId: node.value,
        skipIds: []
      })

      return data.categories.edges.map(mapToItemDataType)
    }

    return []
  }

  const handleChangeSelectedCategories = (category: ItemDataType) => {
    if (category.value && selectedCategories?.some(selCat => selCat.id === category.value)) {
      handleChangePayloadCategories(
        selectedCategories.filter(cat => cat.id !== category.value)
      )
    }
    else if (category.value) {
      const selectedCategory = data.find(cat => cat.value === category.value)
      handleChangePayloadCategories([
        ...(selectedCategories ?? []),
        {
          id: category.value,
          name: selectedCategory?.label as string ?? '',
          order: (selectedCategories?.length ?? 0) + 1,
          previewSize: 'standard'
        }
      ])
    }
  }

  const handleChangeSelectedCategory = (category: SelectedCategoryProps) => {
    handleChangePayloadCategories(selectedCategories.map(cat => {
      if (cat.id === category.id) {
        return category
      }

      return cat
    }))
  }


  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event

    if (active.id !== over?.id && selectedCategories) {

      const oldIndex = selectedCategories.findIndex((i) => i.id === active.id)
      const newIndex = selectedCategories.findIndex((i) => i.id === over?.id)
      const newBlocks = arrayMove(selectedCategories, oldIndex, newIndex)
      handleChangePayloadCategories(newBlocks)
    }
  }

  const handleClear = () => {
    handleChangePayloadCategories([])
  }

  return (
    <div>
      <h4>Выберите категории:</h4>
      <CheckTreePicker
        block
        onOpen={handleLoadRootCategories}
        searchable={true}
        data={data}
        value={selectedCategories.map(cat => cat.id)}
        getChildren={handleGetChildren}
        onClean={handleClear}
        onSelect={handleChangeSelectedCategories}
        placeholder='Категории'
        cascade={false}
      />
      <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        { selectedCategories.map((category) => (
          <SelectedCategory
            key={category.id}
            category={category}
            brandSlug={brandSlug}
            onChange={handleChangeSelectedCategory}
          />
        )) }
      </DndContext>
    </div>
  )
}

const categoryPreviewSizeOptions = [
  {
    label: 'Обычный',
    value: 'normal'
  },
  {
    label: 'Двойной',
    value: 'double'
  },
  {
    label: 'Большой',
    value: 'big'
  }
]

function SelectedCategory ({ 
  category, 
  brandSlug,
  onChange
}: { 
  category: SelectedCategoryProps, 
  brandSlug: string,
  onChange: (category: SelectedCategoryProps) => void 
}) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
    id: category.id,
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    margiBottom: 10,
    marginTop: 10
  }

  return (
    <Panel
      ref={setNodeRef} 
      bordered
      style={style}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div
          {...attributes}
          {...listeners}
          style={{
            cursor: 'grab',
            marginBottom: 10,
            fontWeight: 'bold',
            color: '#555',
          }}
        >
          <div><SortIcon style={{ fontSize: 16 }} />{ category.name }</div>
        </div>
        <EntityLink
          entityId={category.id}
          entityType="category"
          brandSlug={brandSlug}
          tooltip="Перейти к категории"
        />
      </div>
      
      <div style={{ marginBottom: 10 }}>
        <div>Название категории</div>
        <Input
          value={category.name}
          onChange={(value) =>
            onChange({
              ...category,
              name: value
            })
          }
          placeholder="Введите название категории"
        />
      </div>

      <div>Выберите размер</div>
      <SelectPicker
        value={category.previewSize}
        data={categoryPreviewSizeOptions}
        onChange={(value) =>
          onChange({
            ...category,
            previewSize: value as 'standard' | 'double' | 'big'
          })
        }
        searchable={false}
        placeholder="Размер превью"
        valueKey="value"
        labelKey="label"
        cleanable={false}
      />
    </Panel>
  )
}